<script>
  import { onMount } from 'svelte';
  import { getAuth, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';

  let email = '';
  let password = '';
  let error = '';
  let loading = false;
  let clubId = '';

  onMount(() => {
    const urlParams = new URLSearchParams(window.location.search);
    clubId = urlParams.get('clubId') || '';
  });

  const auth = getAuth();

  const getRedirectUrl = () => {
    return clubId ? `/results?clubId=${clubId}` : '/';
  };

  async function handleEmailLogin() {
    try {
      loading = true;
      error = '';
      await signInWithEmailAndPassword(auth, email, password);
      window.location.href = getRedirectUrl();
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  async function handleGoogleLogin() {
    try {
      loading = true;
      error = '';
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      window.location.href = getRedirectUrl();
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  async function handleAppleLogin() {
    try {
      loading = true;
      error = '';
      const provider = new OAuthProvider('apple.com');
      await signInWithPopup(auth, provider);
      window.location.href = getRedirectUrl();
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }
</script>

<div class="login-container">
  <div class="login-card">
    <h2>Admin login</h2>
    <p class="subtitle">Sign in to access the account created through the Dinghy Race timer App</p>
   
    {#if error}
      <div class="error-message">
        {error}
      </div>
    {/if}

    <form on:submit|preventDefault={handleEmailLogin}>
      <div class="input-field">
        <input 
          type="email" 
          id="email" 
          bind:value={email} 
          required
          placeholder="Email"
        />
      </div>

      <div class="input-field">
        <input 
          type="password" 
          id="password" 
          bind:value={password} 
          required
          placeholder="Password"
        />
      </div>

      <button 
        class="btn-large waves-effect waves-light" 
        type="submit"
        disabled={loading}
      >
        {loading ? 'Signing in...' : 'Sign in with Email'}
      </button>
    </form>

    <div class="divider">
      <span>or</span>
    </div>

    <div class="social-login">
      <button 
        class="btn-large waves-effect waves-light google-btn"
        on:click={handleGoogleLogin}
        disabled={loading}
      >
        <img src="/images/google-logo.svg" alt="Google" />
        Sign in with Google
      </button>

      <button 
        class="btn-large waves-effect waves-light apple-btn"
        on:click={handleAppleLogin}
        disabled={loading}
      >
        <img src="/images/apple-logo.svg" alt="Apple" />
        Sign in with Apple
      </button>
    </div>
  </div>
</div>

<style>
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 20px;
  }

  .login-card {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    width: 100%;
    max-width: 600px;
  }

  h2 {
    text-align: center;
    color: #26a69a;
    margin-bottom: 0.25rem;
    font-size: 1.75rem;
  }

  .subtitle {
    text-align: center;
    color: #666;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }

  .input-field {
    margin-bottom: 1rem;
  }

  .input-field input {
    width: 100%;
    padding: 0.6rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.95rem;
  }

  .input-field input:focus {
    border-color: #26a69a;
    box-shadow: 0 0 0 2px rgba(38, 166, 154, 0.2);
  }

  button {
    width: 100%;
    margin-bottom: 0.75rem;
    background-color: #26a69a;
    padding: 0.6rem;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border: none;
    cursor: pointer;
    color: white;
    border-radius: 4px;
    transition: all 0.3s ease;
  }

  button:disabled {
    background-color: #b2dfdb;
    cursor: not-allowed;
  }

  button:hover:not(:disabled) {
    background-color: #2196F3;
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 1rem 0;
  }

  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ddd;
  }

  .divider span {
    padding: 0 1rem;
    color: #666;
    font-size: 0.9rem;
  }

  .social-login {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .google-btn,
  .apple-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.6rem;
    font-size: 0.95rem;
  }

  .google-btn img,
  .apple-btn img {
    width: 20px;
    height: 20px;
  }

  .google-btn {
    background-color: #fff;
    color: #757575;
    border: 1px solid #ddd;
  }

  .apple-btn {
    background-color: #000;
    color: #fff;
  }

  .error-message {
    background-color: #ffebee;
    color: #c62828;
    padding: 0.6rem;
    border-radius: 4px;
    margin-bottom: 0.75rem;
    text-align: center;
    font-size: 0.9rem;
  }

  @media (max-width: 640px) {
    .login-card {
      padding: 1.5rem;
    }
  }
</style>
